/**
 * Browse block: Filterable card grid with dropdown filters
 * Shows filters above content with dynamic filtering.
 *
 * Supports two modes:
 * 1. Inline data: { title, items, filters }
 * 2. Endpoint fetch: { title, endpoint } - fetches data from live section API
 */

import { registerBlockRenderer, getBlockRendererContext, limitArray } from './registry';
import { escapeHtml, createBlockWrapper, disableButtonTemporarily, handleImageError, createErrorState, createEmptyState } from './utils';
import { iconClose, iconChevronDown } from '../icons';
import type { BrowseData, BrowseCard, BrowseFilter, BrowseAction, BrowseSortOption } from './types';
import type { LiveDataItem, LiveSectionResponse } from '../../core/api-client';
import { inlineLoaderHTML } from '../../core/loading/activity-loader';
import { getListStore, type ListItem } from '../../core/list-store';
import { addToCart, getCartPlatform } from '../../core/cart-integration';
import { navigateToUrl } from '../../core/navigation';
import { t } from '../../core/i18n';

// =============================================================================
// BROWSE BLOCK - RENDER SAFETY
// =============================================================================

/** Track containers that have been rendered to prevent infinite loops */
const renderedContainers = new WeakSet<HTMLElement>();

/** Track fetch count per endpoint to prevent infinite fetch loops */
const fetchCounter = new Map<string, { count: number; timestamp: number }>();
const MAX_FETCHES_PER_MINUTE = 10;

/**
 * Check if we should block a fetch for infinite loop prevention
 */
function shouldBlockFetch(endpoint: string): boolean {
  const now = Date.now();
  const entry = fetchCounter.get(endpoint);

  if (!entry || now - entry.timestamp > 60000) {
    // Reset counter after 1 minute
    fetchCounter.set(endpoint, { count: 1, timestamp: now });
    return false;
  }

  entry.count++;
  if (entry.count > MAX_FETCHES_PER_MINUTE) {
    console.error(`[browse] Too many fetches for endpoint: ${endpoint} - blocking to prevent infinite loop`);
    return true;
  }

  return false;
}

// =============================================================================
// BROWSE BLOCK
// =============================================================================

registerBlockRenderer('browse', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  // SAFETY: Prevent re-rendering the same container
  if (renderedContainers.has(container)) {
    console.warn('[browse] Container already rendered - skipping to prevent infinite loop');
    return;
  }
  renderedContainers.add(container);

  const browseData = data as BrowseData;
  const anyData = data as Record<string, unknown>;
  const ctx = getBlockRendererContext();

  // Handle missing or invalid data
  if (!browseData) {
    console.warn('[browse] Invalid browse data - no data provided');
    return;
  }

  const title = browseData.title || '';
  const endpoint = anyData.endpoint as string | undefined;

  // Check if we have inline items - can be in items OR initialFilters.items (AI sometimes puts data there)
  const initialFilters = anyData.initialFilters as Record<string, unknown> | undefined;
  const itemsFromInitialFilters = initialFilters?.items as BrowseCard[] | undefined;
  const hasInlineItems = browseData.items && Array.isArray(browseData.items) && browseData.items.length > 0;
  const hasItemsInFilters = itemsFromInitialFilters && Array.isArray(itemsFromInitialFilters) && itemsFromInitialFilters.length > 0;

  console.log('[browse] Mode detection:', {
    hasInlineItems,
    hasItemsInFilters,
    endpoint,
    mode: (hasInlineItems || hasItemsInFilters) ? 'INLINE' : (endpoint ? 'ENDPOINT' : 'EMPTY'),
    itemCount: hasInlineItems ? browseData.items!.length : (hasItemsInFilters ? itemsFromInitialFilters!.length : 0),
    firstInlineItemImage: hasInlineItems && browseData.items![0] ? (browseData.items![0] as { image?: string }).image : undefined
  });

  // If AI put items in initialFilters.items, use them as inline items
  if (hasItemsInFilters && !hasInlineItems) {
    console.log('[browse] Found items in initialFilters.items, using as inline data');
    // Items are already formatted as BrowseCard by Claude
    const items = limitArray(itemsFromInitialFilters! as BrowseCard[], 50).map(normalizeInlineBrowseCard);
    const filters = limitArray(browseData.filters || [], 10);
    renderBrowseContent(items, filters, title, browseData, container, onAction);
    return;
  }

  if (!hasInlineItems && endpoint) {
    // ENDPOINT MODE: Fetch data from live section API
    console.log('[browse] Using ENDPOINT mode, fetching from:', endpoint);
    renderWithEndpointFetch(endpoint, title, browseData, container, onAction, ctx);
    return;
  }

  if (!hasInlineItems) {
    console.warn('[browse] No items to display. Data keys:', Object.keys(browseData));
    const wrapper = createBlockWrapper('browse');
    wrapper.innerHTML = createEmptyState('No items to display', { compact: true });
    container.appendChild(wrapper);
    return;
  }

  // INLINE MODE: Render with existing items (already formatted as BrowseCard by Claude)
  // SAFETY: Limit array sizes to prevent DOM explosion
  const items = limitArray(browseData.items!, 50).map(normalizeInlineBrowseCard);
  const filters = limitArray(browseData.filters || [], 10);
  console.log('[browse] INLINE mode:', browseData.items!.length, 'items, rendering', items.length);
  renderBrowseContent(items, filters, title, browseData, container, onAction);
});

/**
 * Lift non-string metadata (description_paragraphs) out of an inline card's
 * metadata bag so the renderer can use it. The BrowseCard metadata type is
 * `Record<string, string>`, but the backend enrichment packs in structured
 * arrays that would otherwise be string-coerced into garbage or lost.
 */
function normalizeInlineBrowseCard(card: BrowseCard): BrowseCard {
  const rawMeta = (card as unknown as { metadata?: Record<string, unknown> }).metadata;
  if (!rawMeta || typeof rawMeta !== 'object') return card;

  let paragraphs: string[] | undefined = card.descriptionParagraphs;
  if (!paragraphs && Array.isArray(rawMeta['description_paragraphs'])) {
    paragraphs = (rawMeta['description_paragraphs'] as unknown[])
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  }

  // Rebuild metadata as scalars only so Record<string, string> invariants hold
  // downstream (filtering, rendering chips, etc.).
  const cleanMeta: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawMeta)) {
    if (value === undefined || value === null) continue;
    if (key === 'description_paragraphs') continue;
    if (Array.isArray(value) || typeof value === 'object') continue;
    cleanMeta[key] = String(value);
  }

  return {
    ...card,
    descriptionParagraphs: paragraphs && paragraphs.length > 0 ? paragraphs : card.descriptionParagraphs,
    metadata: Object.keys(cleanMeta).length > 0 ? cleanMeta : undefined,
  };
}

/**
 * Extract section ID from various endpoint formats:
 * - "properties" -> "properties"
 * - "/api/workspaces/{workspaceId}/live-data/properties" -> "properties"
 * - "/api/workspaces/{workspaceId}/sections/properties/data" -> "properties"
 * - "https://example.com/api/products" -> null (external API, not supported yet)
 */
function extractSectionId(endpoint: string): string | null {
  // Already a clean section ID (no slashes, no URL)
  if (!endpoint.includes('/') && !endpoint.includes(':')) {
    return endpoint;
  }

  // External URL - not supported yet for internal sections
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    console.warn('[browse] External API endpoints not yet supported:', endpoint);
    return null;
  }

  // Try to extract section ID from various path formats
  // Format: /api/workspaces/{workspaceId}/live-data/{sectionId}
  const liveDataMatch = endpoint.match(/\/live-data\/([^/]+)/);
  if (liveDataMatch && liveDataMatch[1]) {
    return liveDataMatch[1];
  }

  // Format: /api/workspaces/{workspaceId}/sections/{sectionId}/data
  const sectionsMatch = endpoint.match(/\/sections\/([^/]+)/);
  if (sectionsMatch && sectionsMatch[1]) {
    return sectionsMatch[1];
  }

  // Last path segment as fallback
  const segments = endpoint.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];

  // Skip common non-section endings
  if (lastSegment && !['data', 'api', 'workspaces'].includes(lastSegment)) {
    return lastSegment;
  }

  console.warn('[browse] Could not extract section ID from endpoint:', endpoint);
  return null;
}

/**
 * Fetch data from endpoint and render
 */
async function renderWithEndpointFetch(
  endpoint: string,
  title: string,
  browseData: BrowseData,
  container: HTMLElement,
  onAction?: (value: string) => void,
  ctx?: { apiClient: unknown; workspaceId: string | null }
): Promise<void> {
  // SAFETY: Check for infinite fetch loop
  if (shouldBlockFetch(endpoint)) {
    const wrapper = createBlockWrapper('browse');
    wrapper.innerHTML = createErrorState('This content is temporarily unavailable', { compact: true });
    container.appendChild(wrapper);
    return;
  }

  const wrapper = createBlockWrapper('browse');

  // Show loading state
  wrapper.innerHTML = `
    <div class="pms-browse-header">
      <div class="pms-browse-title">
        <span class="pms-browse-title-text">${escapeHtml(title)}</span>
      </div>
    </div>
    <div class="pms-browse-loading">
      ${inlineLoaderHTML('Loading...')}
    </div>
  `;
  container.appendChild(wrapper);

  // Extract section ID from endpoint (handles various formats)
  const sectionId = extractSectionId(endpoint);
  if (!sectionId) {
    console.error('[browse] Invalid endpoint format:', endpoint);
    wrapper.innerHTML = createErrorState('This content is not available right now', { compact: true });
    return;
  }

  // Check if we have API context
  if (!ctx?.apiClient || !ctx?.workspaceId) {
    console.error('[browse] Cannot fetch from endpoint: missing API context');
    wrapper.innerHTML = createErrorState('This content is not available right now', { compact: true });
    return;
  }

  try {
    // Get initial filters from browse data (AI can pre-set these based on user query)
    const initialFilters = (browseData as unknown as { initialFilters?: Record<string, string> }).initialFilters || {};

    // Fetch data from live section API using extracted section ID
    // Pass initialFilters to backend for SQL-based smart filtering
    const apiClient = ctx.apiClient as { getLiveSectionData: (workspaceId: string, sectionId: string, filters: Record<string, string>, limit: number) => Promise<LiveSectionResponse> };

    console.log('[browse] Fetching with filters:', initialFilters);

    const response = await apiClient.getLiveSectionData(
      ctx.workspaceId,
      sectionId,
      initialFilters,  // Pass filters to backend for SQL-based filtering
      100  // Limit
    );

    // DEBUG: Log first item to check imageUrl
    const firstItem = response.items[0];
    if (firstItem) {
      console.log('[browse] First item from API:', {
        id: firstItem.id,
        title: firstItem.title,
        imageUrl: firstItem.imageUrl,
        hasImageUrl: !!firstItem.imageUrl
      });
    }

    // Map live section items to browse cards
    // SAFETY: Limit items from API to prevent DOM explosion
    const limitedItems = limitArray(response.items, 50);
    const items: BrowseCard[] = limitedItems.map((item: LiveDataItem) => {
      // Pull non-string metadata values out BEFORE string coercion so we don't
      // flatten arrays/objects into garbage like "a,b,c".
      let descriptionParagraphs: string[] | undefined;
      const rawMeta = item.metadata as Record<string, unknown> | undefined;
      if (rawMeta && Array.isArray(rawMeta['description_paragraphs'])) {
        descriptionParagraphs = (rawMeta['description_paragraphs'] as unknown[])
          .filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
      }

      // Convert metadata to string values (excluding structured fields we lifted above)
      const stringMetadata: Record<string, string> = {};
      if (item.metadata) {
        for (const [key, value] of Object.entries(item.metadata)) {
          if (value === undefined || value === null) continue;
          if (key === 'description_paragraphs') continue; // lifted above
          if (Array.isArray(value) || (typeof value === 'object')) continue; // skip non-scalar
          stringMetadata[key] = String(value);
        }
      }
      // IMPORTANT: Merge filterValues into metadata for client-side filtering
      // Filter definitions come from filterValues, so we need them in metadata for matching
      if (item.filterValues) {
        for (const [key, value] of Object.entries(item.filterValues)) {
          if (value !== undefined && value !== null) {
            stringMetadata[key] = String(value);
          }
        }
      }
      // Add category/subcategory to metadata if present
      if (item.category) stringMetadata['category'] = item.category;
      if (item.subcategory) stringMetadata['subcategory'] = item.subcategory;

      // Build tags from category/subcategory
      const tags: string[] = [];
      if (item.category) tags.push(item.category);
      if (item.subcategory) tags.push(item.subcategory);

      const card: BrowseCard = {
        id: item.id,
        title: item.title,
        description: item.description,
        descriptionParagraphs: descriptionParagraphs && descriptionParagraphs.length > 0 ? descriptionParagraphs : undefined,
        image: item.imageUrl,
        badge: item.badge,
        price: item.price,
        originalPrice: stringMetadata['original_price'] || undefined,
        metadata: Object.keys(stringMetadata).length > 0 ? stringMetadata : undefined,
        tags: tags.length > 0 ? tags : undefined,
        action: item.actionConfig ? {
          label: item.actionConfig.label || 'View',
          value: item.actionConfig.value,
          url: item.actionConfig.url
        } : undefined
      };
      // DEBUG: Log image mapping
      if (!card.image && item.imageUrl) {
        console.warn('[browse] Image lost during mapping!', { itemImageUrl: item.imageUrl, cardImage: card.image });
      }
      return card;
    });

    // Map filters
    // SAFETY: Limit filters from API
    const limitedFilters = limitArray(response.filters || [], 10);
    const filters: BrowseFilter[] = limitedFilters.map(f => ({
      id: f.id,
      label: f.label,
      options: limitArray(f.options.map(o => o.value), 50)
    }));

    // Clear loading state and render content
    wrapper.innerHTML = '';
    renderBrowseContent(items, filters, title, browseData, wrapper, onAction, true);

  } catch (error) {
    console.error('[browse] Failed to fetch from endpoint:', error);
    wrapper.innerHTML = `<div class="pms-browse-error">Failed to load data. Please try again.</div>`;
  }
}

/**
 * Render browse content with items and filters
 */
function renderBrowseContent(
  items: BrowseCard[],
  filters: BrowseFilter[],
  title: string,
  browseData: BrowseData,
  container: HTMLElement,
  onAction?: (value: string) => void,
  useContainerDirectly = false
): void {
  const wrapper = useContainerDirectly ? container : createBlockWrapper('browse');

  // Auto-detect selection mode: explicit OR if any action mentions "selected"
  const hasSelectionActions = browseData.actions?.some(a =>
    a.label.toLowerCase().includes('selected') ||
    a.value.toLowerCase().includes('selected') ||
    a.requiresSelection === true
  );
  const isSelectable = browseData.selectable === true || hasSelectionActions === true;

  // Track active filters: filterId -> selected value (empty string = all)
  const activeFilters: Map<string, string> = new Map();

  // Track selected items (by card ID or index)
  const selectedItems = new Set<string>();

  // Get initial filters from data (AI can pre-set these based on user query)
  const initialFilters = (browseData as unknown as { initialFilters?: Record<string, string> }).initialFilters || {};

  // Initialize filter state - apply initial filters if provided
  for (const filter of filters) {
    // Check if there's an initial filter value for this filter
    // Try both exact match and case-insensitive match
    let initialValue = '';
    if (initialFilters[filter.id] !== undefined) {
      initialValue = initialFilters[filter.id] ?? '';
    } else {
      // Try case-insensitive lookup
      const lowerFilterId = filter.id.toLowerCase();
      for (const [key, value] of Object.entries(initialFilters)) {
        if (key.toLowerCase() === lowerFilterId) {
          initialValue = value;
          break;
        }
      }
    }
    activeFilters.set(filter.id, initialValue);
  }

  // Track if we need to apply initial filters after render
  const hasInitialFilters = Object.keys(initialFilters).length > 0;

  // Header with title and count (skip if no title)
  if (title) {
    const header = document.createElement('div');
    header.className = 'pms-browse-header';

    const titleEl = document.createElement('div');
    titleEl.className = 'pms-browse-title';
    titleEl.innerHTML = `<span class="pms-browse-title-text">${escapeHtml(title)}</span><span class="pms-browse-count">(${items.length})</span>`;
    header.appendChild(titleEl);

    // Selection count indicator (shown when items selected)
    if (isSelectable) {
      const selectionIndicator = document.createElement('span');
      selectionIndicator.className = 'pms-browse-selection-count';
      selectionIndicator.style.display = 'none';
      titleEl.appendChild(selectionIndicator);
    }

    wrapper.appendChild(header);
  }

  // Filters container - now using dropdowns
  const filtersWrapper = document.createElement('div');
  filtersWrapper.className = 'pms-browse-filters';

  for (const filter of filters) {
    const filterGroup = document.createElement('div');
    filterGroup.className = 'pms-browse-filter-item';

    const filterLabel = document.createElement('label');
    filterLabel.className = 'pms-browse-filter-label';
    filterLabel.textContent = filter.label;
    filterGroup.appendChild(filterLabel);

    // Create select dropdown
    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'pms-browse-select-wrapper';

    const select = document.createElement('select');
    select.className = 'pms-browse-select';
    select.dataset['filterId'] = filter.id;

    // Get initial value for this filter (if any)
    const initialValue = activeFilters.get(filter.id) || '';

    // Add "All" option
    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = 'All';
    allOption.selected = initialValue === '';
    select.appendChild(allOption);

    // Add filter options
    let initialValueMatched = !initialValue; // true if no initial value set
    for (const option of filter.options) {
      const optEl = document.createElement('option');
      // Ensure option is converted to string (may be number from backend)
      const optStr = String(option);
      optEl.value = optStr;
      optEl.textContent = optStr;
      // Pre-select if matches initial filter (case-insensitive)
      if (optStr.toLowerCase() === String(initialValue).toLowerCase()) {
        optEl.selected = true;
        initialValueMatched = true;
      }
      select.appendChild(optEl);
    }

    // If initial filter value doesn't match any predefined option (e.g., AI sent "under 1000000"),
    // reset to "All" — the backend already filtered the data via SQL, so client-side
    // filtering isn't needed. Leaving the unmatched value would cause applyFilters() to
    // hide all cards since string matching can't handle numeric range comparisons.
    if (!initialValueMatched && initialValue) {
      console.log(`[browse] Initial filter "${filter.id}"="${initialValue}" not in dropdown options — backend already filtered, resetting to All`);
      activeFilters.set(filter.id, '');
      allOption.selected = true;
    }

    // Add chevron icon
    const chevron = document.createElement('span');
    chevron.className = 'pms-browse-select-chevron';
    chevron.innerHTML = iconChevronDown();

    selectWrapper.appendChild(select);
    selectWrapper.appendChild(chevron);
    filterGroup.appendChild(selectWrapper);
    filtersWrapper.appendChild(filterGroup);
  }

  // Sort dropdown (if sort options provided)
  const sortOptions: BrowseSortOption[] = browseData.sortOptions || [];
  if (sortOptions.length > 0) {
    const sortGroup = document.createElement('div');
    sortGroup.className = 'pms-browse-filter-item pms-browse-sort-item';

    const sortLabel = document.createElement('label');
    sortLabel.className = 'pms-browse-filter-label';
    sortLabel.textContent = t('blocks.sort');
    sortGroup.appendChild(sortLabel);

    const sortSelectWrapper = document.createElement('div');
    sortSelectWrapper.className = 'pms-browse-select-wrapper';

    const sortSelect = document.createElement('select');
    sortSelect.className = 'pms-browse-select pms-browse-sort-select';

    const sortI18nKeys: Record<string, string> = {
      'price_asc': 'blocks.priceLowToHigh',
      'price_desc': 'blocks.priceHighToLow',
      'title_asc': 'blocks.nameAToZ',
    };
    for (const opt of sortOptions) {
      const optEl = document.createElement('option');
      optEl.value = opt.id;
      const i18nKey = sortI18nKeys[opt.id];
      optEl.textContent = i18nKey ? t(i18nKey) : opt.label;
      if (opt.id === browseData.defaultSort) optEl.selected = true;
      sortSelect.appendChild(optEl);
    }

    const sortChevron = document.createElement('span');
    sortChevron.className = 'pms-browse-select-chevron';
    sortChevron.innerHTML = iconChevronDown();

    sortSelectWrapper.appendChild(sortSelect);
    sortSelectWrapper.appendChild(sortChevron);
    sortGroup.appendChild(sortSelectWrapper);
    filtersWrapper.appendChild(sortGroup);
  }

  // "Hide out of stock" toggle — shown only when OOS items exist
  let hideOutOfStock = false;
  const hasOosItems = items.some(item => item.badge && item.badge.toLowerCase().includes('out of stock'));
  if (hasOosItems) {
    const oosToggle = document.createElement('label');
    oosToggle.className = 'pms-browse-oos-toggle';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'pms-browse-oos-checkbox';
    checkbox.addEventListener('change', () => {
      hideOutOfStock = checkbox.checked;
      applyFilters();
    });
    oosToggle.appendChild(checkbox);
    oosToggle.appendChild(document.createTextNode(` ${t('blocks.hideOutOfStock')}`));
    filtersWrapper.appendChild(oosToggle);
  }

  // Only append filters if there are any (includes sort, OOS toggle)
  if (filters.length > 0 || sortOptions.length > 0 || hasOosItems) {
    wrapper.appendChild(filtersWrapper);
  }

  // Cards container - use only browse-specific classes (not pms-cards-grid which has view overrides)
  const cardsWrapper = document.createElement('div');
  cardsWrapper.className = `pms-browse-cards${browseData.layout === 'list' ? ' pms-browse-cards-list' : ''}`;

  // Render all cards
  items.forEach((item, index) => {
    const cardId = item.id || `card-${index}`;
    const card = renderBrowseCard(item, cardId, isSelectable, selectedItems, onAction, updateSelectionState);
    // Store filterable and sortable data on the card element
    card.dataset['tags'] = (item.tags || []).join(',').toLowerCase();
    card.dataset['cardId'] = cardId;
    card.dataset['sortTitle'] = (item.title || '').toLowerCase();
    // Mark out-of-stock cards for toggle filtering
    if (item.badge && item.badge.toLowerCase().includes('out of stock')) {
      card.dataset['outOfStock'] = 'true';
    }
    // Parse numeric price for sorting (strip currency symbols, commas)
    if (item.price) {
      const priceMatch = item.price.toString().match(/([\d,]+(?:\.\d+)?)/);
      if (priceMatch?.[1]) {
        card.dataset['sortPrice'] = String(parseFloat(priceMatch[1].replace(/,/g, '')));
      }
    }
    if (item.metadata) {
      // Handle both array format [{label, value}] and object format {key: value}
      if (Array.isArray(item.metadata)) {
        for (const meta of item.metadata) {
          if (meta && typeof meta === 'object' && 'label' in meta && 'value' in meta) {
            const label = String(meta.label || '');
            const val = String(meta.value || '');
            if (label) {
              const camelKey = 'filter' + label.charAt(0).toUpperCase() + label.slice(1).toLowerCase().replace(/\s+/g, '');
              card.dataset[camelKey] = val.toLowerCase();
            }
          }
        }
      } else {
        for (const [key, value] of Object.entries(item.metadata)) {
          if (typeof value === 'string') {
            const camelKey = 'filter' + key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
            card.dataset[camelKey] = value.toLowerCase();
          }
        }
      }
    }
    cardsWrapper.appendChild(card);
  });

  wrapper.appendChild(cardsWrapper);

  // Actions section (if provided)
  let actionsWrapper: HTMLElement | null = null;
  if (browseData.actions && browseData.actions.length > 0) {
    actionsWrapper = document.createElement('div');
    actionsWrapper.className = 'pms-browse-actions';

    for (const action of browseData.actions) {
      // Auto-detect action type based on label/value
      const labelLower = action.label.toLowerCase();
      const valueLower = action.value.toLowerCase();
      const mentionsSelected = labelLower.includes('selected') || valueLower.includes('selected');
      const isLocalAction = action.type === 'local' || mentionsSelected;
      const requiresSelection = action.requiresSelection === true || mentionsSelected;

      const btn = document.createElement('button');
      btn.className = 'pms-browse-action-btn';
      if (isLocalAction) {
        btn.classList.add('pms-browse-action-local');
      }
      btn.textContent = action.label;
      btn.dataset['action'] = action.value;
      btn.dataset['actionType'] = isLocalAction ? 'local' : 'navigate';
      btn.dataset['requiresSelection'] = requiresSelection ? 'true' : 'false';

      // Disable buttons that require selection initially
      if (requiresSelection) {
        btn.disabled = true;
        btn.classList.add('pms-browse-action-disabled');
      }

      btn.addEventListener('click', () => {
        if (isLocalAction) {
          handleLocalAction(action, selectedItems, items);
        } else if (onAction) {
          disableButtonTemporarily(btn, 2000);
          onAction(action.value);
        }
      });

      actionsWrapper.appendChild(btn);
    }

    wrapper.appendChild(actionsWrapper);
  }

  // Only append if we created a new wrapper
  if (!useContainerDirectly) {
    container.appendChild(wrapper);
  }

  // Filter change handler
  filtersWrapper.addEventListener('change', (e) => {
    const target = e.target as HTMLSelectElement;
    if (!target.classList.contains('pms-browse-select')) return;

    const filterId = target.dataset['filterId'];
    const value = target.value;
    if (!filterId) return;

    activeFilters.set(filterId, value);
    applyFilters();
  });

  // Sort change handler
  const sortSelect = filtersWrapper.querySelector('.pms-browse-sort-select') as HTMLSelectElement | null;
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      applySorting(sortSelect.value);
    });
    // Apply default sort if set
    if (browseData.defaultSort) {
      applySorting(browseData.defaultSort);
    }
  }

  /**
   * Reorder visible cards by sort criteria
   */
  function applySorting(sortId: string): void {
    const cards = Array.from(cardsWrapper.querySelectorAll('.pms-browse-card')) as HTMLElement[];

    cards.sort((a, b) => {
      switch (sortId) {
        case 'price_asc': {
          const pa = parseFloat(a.dataset['sortPrice'] || '999999');
          const pb = parseFloat(b.dataset['sortPrice'] || '999999');
          return pa - pb;
        }
        case 'price_desc': {
          const pa = parseFloat(a.dataset['sortPrice'] || '0');
          const pb = parseFloat(b.dataset['sortPrice'] || '0');
          return pb - pa;
        }
        case 'title_asc': {
          const ta = a.dataset['sortTitle'] || '';
          const tb = b.dataset['sortTitle'] || '';
          return ta.localeCompare(tb);
        }
        case 'title_desc': {
          const ta = a.dataset['sortTitle'] || '';
          const tb = b.dataset['sortTitle'] || '';
          return tb.localeCompare(ta);
        }
        default:
          return 0; // No sort (original order)
      }
    });

    // Re-append in sorted order (DOM reflow)
    for (const card of cards) {
      cardsWrapper.appendChild(card);
    }
  }

  // Apply initial filters immediately if any were provided (e.g., "3 bedroom" query)
  if (hasInitialFilters) {
    // Log which filters matched and which didn't
    for (const [key, value] of Object.entries(initialFilters)) {
      const matchedFilter = filters.find(f => f.id.toLowerCase() === key.toLowerCase());
      if (matchedFilter) {
        // Ensure we compare as strings (options may be numbers from backend)
        const matchedOption = matchedFilter.options.find(o => String(o).toLowerCase() === String(value).toLowerCase());
        if (matchedOption) {
          console.log(`[browse] Initial filter applied: ${key}="${value}"`);
        } else {
          console.warn(`[browse] Initial filter value not found: ${key}="${value}". Available options: [${matchedFilter.options.join(', ')}]`);
        }
      } else {
        console.warn(`[browse] Initial filter key not found: "${key}". Available filters: [${filters.map(f => f.id).join(', ')}]`);
      }
    }
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => applyFilters(), 0);
  }

  /**
   * Update selection state and UI
   */
  function updateSelectionState(): void {
    const count = selectedItems.size;

    // Update selection count indicator
    const selectionIndicator = wrapper.querySelector('.pms-browse-selection-count') as HTMLElement;
    if (selectionIndicator) {
      if (count > 0) {
        selectionIndicator.textContent = ` • ${count} selected`;
        selectionIndicator.style.display = '';
      } else {
        selectionIndicator.style.display = 'none';
      }
    }

    // Update action button states
    if (actionsWrapper) {
      const buttons = actionsWrapper.querySelectorAll('.pms-browse-action-btn');
      buttons.forEach(btnEl => {
        const btn = btnEl as HTMLButtonElement;
        if (btn.dataset['requiresSelection'] === 'true') {
          btn.disabled = count === 0;
          btn.classList.toggle('pms-browse-action-disabled', count === 0);
        }
      });
    }
  }

  /**
   * Handle local actions (within component)
   */
  function handleLocalAction(action: BrowseAction, selected: Set<string>, allItems: BrowseCard[]): void {
    const selectedItemData = allItems.filter((item, i) => {
      const id = item.id || `card-${i}`;
      return selected.has(id);
    });

    if (selectedItemData.length === 0) {
      console.warn('[browse] Local action triggered with no selection:', action.value);
      return;
    }

    // Handle common local actions
    const actionValue = action.value.toLowerCase();

    if (actionValue.includes('compare')) {
      // Show comparison view
      showComparisonView(selectedItemData, wrapper);
    } else if (actionValue.includes('contact') || actionValue.includes('email')) {
      // Trigger contact action with selected items - this CAN go to AI
      if (onAction) {
        const names = selectedItemData.map(i => i.title).join(', ');
        onAction(`I want to contact: ${names}`);
      }
    } else {
      // Generic local action - pass to onAction with context
      if (onAction) {
        const ids = selectedItemData.map(i => i.id || i.title).join(', ');
        onAction(`${action.value}: ${ids}`);
      }
    }
  }

  /**
   * Apply current filters to show/hide cards
   */
  function applyFilters(): void {
    const cards = cardsWrapper.querySelectorAll('.pms-browse-card');
    let visibleCount = 0;

    cards.forEach(cardEl => {
      const card = cardEl as HTMLElement;
      let visible = true;

      // Hide out-of-stock items if toggle is checked
      if (hideOutOfStock && card.dataset['outOfStock'] === 'true') {
        visible = false;
      }

      // Check each filter
      if (visible) {
        for (const [filterId, selectedValue] of activeFilters.entries()) {
          if (!selectedValue) continue; // No filter applied (All selected)

          // Get card's value for this filter
          const camelKey = 'filter' + filterId.charAt(0).toUpperCase() + filterId.slice(1).toLowerCase();
          const cardValue = card.dataset[camelKey] || '';
          const cardTags = card.dataset['tags'] || '';

          const lowerVal = selectedValue.toLowerCase();
          if (!cardValue.includes(lowerVal) && !cardTags.includes(lowerVal)) {
            visible = false;
            break;
          }
        }
      }

      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Update count
    const countEl = wrapper.querySelector('.pms-browse-count');
    if (countEl) {
      countEl.textContent = `(${visibleCount})`;
    }
  }
}

/**
 * Show side-by-side comparison of selected items
 */
function showComparisonView(items: BrowseCard[], container: HTMLElement): void {
  // Create comparison overlay
  const overlay = document.createElement('div');
  overlay.className = 'pms-browse-comparison-overlay';

  const compareContainer = document.createElement('div');
  compareContainer.className = 'pms-browse-comparison';

  // Header with close button
  const header = document.createElement('div');
  header.className = 'pms-browse-comparison-header';
  header.innerHTML = `
    <h3>Compare (${items.length})</h3>
    <button class="pms-browse-comparison-close" aria-label="Close comparison">${iconClose()}</button>
  `;
  compareContainer.appendChild(header);

  // Comparison grid
  const grid = document.createElement('div');
  grid.className = 'pms-browse-comparison-grid';
  grid.style.gridTemplateColumns = `repeat(${Math.min(items.length, 4)}, 1fr)`;

  for (const item of items) {
    const col = document.createElement('div');
    col.className = 'pms-browse-comparison-item';

    let html = '';
    if (item.image) {
      html += `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title || '')}" class="pms-browse-comparison-image" loading="lazy" decoding="async" />`;
    }
    html += `<h4>${escapeHtml(item.title || '')}</h4>`;
    if (item.description) {
      html += `<p>${escapeHtml(item.description)}</p>`;
    }
    if (item.metadata) {
      html += '<dl class="pms-browse-comparison-meta">';
      if (Array.isArray(item.metadata)) {
        for (const meta of item.metadata) {
          if (meta && typeof meta === 'object' && 'label' in meta && 'value' in meta) {
            html += `<dt>${escapeHtml(String(meta.label || ''))}</dt><dd>${escapeHtml(String(meta.value || ''))}</dd>`;
          }
        }
      } else {
        for (const [key, value] of Object.entries(item.metadata)) {
          html += `<dt>${escapeHtml(key)}</dt><dd>${escapeHtml(String(value))}</dd>`;
        }
      }
      html += '</dl>';
    }

    col.innerHTML = html;
    grid.appendChild(col);
  }

  compareContainer.appendChild(grid);
  overlay.appendChild(compareContainer);

  // Close handler
  const closeComparison = () => overlay.remove();
  overlay.querySelector('.pms-browse-comparison-close')?.addEventListener('click', closeComparison);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeComparison();
  });

  container.appendChild(overlay);
}

/**
 * Render a browse card with improved layout and optional selection
 */
function renderBrowseCard(
  item: BrowseCard,
  cardId: string,
  isSelectable: boolean,
  selectedItems: Set<string>,
  onAction?: (value: string) => void,
  onSelectionChange?: () => void
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'pms-browse-card';

  // Clean "tell me about" message — backend already has product data, no need for [IMAGE:] in chat
  const defaultActionValue = `tell me about ${item.title}`;

  // Selection checkbox (if selectable)
  if (isSelectable) {
    const selectArea = document.createElement('div');
    selectArea.className = 'pms-browse-card-select';
    selectArea.innerHTML = `
      <label class="pms-browse-checkbox-wrapper">
        <input type="checkbox" class="pms-browse-checkbox" />
        <span class="pms-browse-checkbox-mark"></span>
      </label>
    `;

    const checkbox = selectArea.querySelector('input') as HTMLInputElement;
    checkbox.checked = selectedItems.has(cardId);

    checkbox.addEventListener('change', (e) => {
      e.stopPropagation();
      if (checkbox.checked) {
        selectedItems.add(cardId);
        card.classList.add('pms-browse-card-selected');
      } else {
        selectedItems.delete(cardId);
        card.classList.remove('pms-browse-card-selected');
      }
      onSelectionChange?.();
    });

    card.appendChild(selectArea);
  }

  // Image at top if provided
  if (item.image) {
    const imageWrapper = document.createElement('div');
    imageWrapper.className = 'pms-browse-card-image-wrapper';
    const img = document.createElement('img');
    img.className = 'pms-browse-card-image';
    img.src = item.image;
    img.alt = item.title || '';
    img.loading = 'lazy';
    img.decoding = 'async';

    // Handle image error - hide entire card if image fails
    handleImageError(img, false); // Don't use pms-card class detection
    img.addEventListener('error', () => {
      card.classList.add('pms-browse-card-image-error');
    }, { once: true });
    // Check if already failed
    if (img.complete && img.naturalWidth === 0) {
      card.classList.add('pms-browse-card-image-error');
    }

    imageWrapper.appendChild(img);
    card.appendChild(imageWrapper);
  }

  // Card inner content wrapper for better spacing
  const cardContent = document.createElement('div');
  cardContent.className = 'pms-browse-card-content';

  let html = '';

  // Badge
  if (item.badge) {
    html += `<div class="pms-browse-card-badge">${escapeHtml(item.badge)}</div>`;
  }

  // Title
  if (item.title) {
    html += `<h3 class="pms-browse-card-title">${escapeHtml(item.title)}</h3>`;
  }

  // Subtitle (secondary context)
  if (item.subtitle) {
    html += `<p class="pms-browse-card-subtitle">${escapeHtml(item.subtitle)}</p>`;
  }

  // Description
  if (item.description) {
    html += `<p class="pms-browse-card-desc">${escapeHtml(item.description)}</p>`;
  }

  // Styled spec list — compact bullets from the structured description split.
  // Capped so a product with 40 spec lines doesn't explode the card height.
  if (item.descriptionParagraphs && item.descriptionParagraphs.length > 0) {
    const SPEC_DISPLAY_LIMIT = 4;
    const SPEC_ITEM_CHAR_LIMIT = 90;
    const visible = item.descriptionParagraphs.slice(0, SPEC_DISPLAY_LIMIT);
    html += '<ul class="pms-browse-card-specs">';
    for (const para of visible) {
      const trimmed = para.length > SPEC_ITEM_CHAR_LIMIT
        ? para.slice(0, SPEC_ITEM_CHAR_LIMIT).trim() + '…'
        : para;
      html += `<li class="pms-browse-card-spec">${escapeHtml(trimmed)}</li>`;
    }
    html += '</ul>';
    const remaining = item.descriptionParagraphs.length - visible.length;
    if (remaining > 0) {
      html += `<div class="pms-browse-card-specs-more">+${remaining} more</div>`;
    }
  }

  // Stats (key metrics as chips)
  if (item.stats && item.stats.length > 0) {
    html += '<div class="pms-browse-card-stats">';
    for (const stat of item.stats) {
      if (stat.label && stat.value) {
        html += `<span class="pms-browse-card-stat"><span class="pms-browse-card-stat-label">${escapeHtml(stat.label)}:</span> ${escapeHtml(stat.value)}</span>`;
      }
    }
    html += '</div>';
  }

  // Price - skip if empty, null, 0, or zero with currency symbol
  const priceStr = item.price?.toString().trim() || '';
  const isZeroPrice = /^[£$€]?0(\.00)?$/.test(priceStr) || priceStr === '0';
  if (item.price && !isZeroPrice) {
    if (item.originalPrice) {
      // Sale item: strikethrough original + highlighted current price
      html += `<div class="pms-browse-card-price pms-browse-card-price-sale">`;
      html += `<span class="pms-browse-card-price-was">${escapeHtml(item.originalPrice)}</span>`;
      html += `<span class="pms-browse-card-price-now">${escapeHtml(item.price)}</span>`;
      html += `</div>`;
    } else {
      html += `<div class="pms-browse-card-price">${escapeHtml(item.price)}</div>`;
    }
  }

  cardContent.innerHTML = html;
  card.appendChild(cardContent);

  // Metadata tags in separate footer area - filter out bad values
  if (item.metadata && Object.keys(item.metadata).length > 0) {
    // Filter metadata - skip bad values
    const goodMeta = Object.entries(item.metadata).filter(([key, value]) => {
      if (!key || !value) return false;
      const strValue = String(value).toLowerCase().trim();
      // Skip boolean-like values
      if (strValue === 'true' || strValue === 'false') return false;
      // Skip very short numeric-only values (like "2", "3", "4")
      if (/^\d{1,2}$/.test(strValue)) return false;
      // Skip generic/internal keys
      const skipKeys = ['id', 'slug', 'created', 'updated', 'raw', 'category', 'subcategory',
        'compare_at_price', 'on_sale', 'discount_percent', 'original_price',
        'product_id', 'variant_id', 'handle', 'has_selling_plans', 'selling_plan_summary',
        'inventory_quantity', 'in_stock', 'selected_options', 'selling_plan_groups',
        'sales_count_60d'];
      if (skipKeys.includes(key.toLowerCase())) return false;
      return true;
    });

    if (goodMeta.length > 0) {
      const metaWrapper = document.createElement('div');
      metaWrapper.className = 'pms-browse-card-meta';

      for (const [key, value] of goodMeta) {
        const tag = document.createElement('span');
        tag.className = 'pms-browse-card-tag';
        // Format: "Key: Value" or just "Value" for location-like keys
        const displayKey = key.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const locationKeys = ['location', 'address', 'area'];
        if (locationKeys.includes(key.toLowerCase())) {
          tag.textContent = value;
        } else {
          tag.textContent = `${displayKey}: ${value}`;
        }
        metaWrapper.appendChild(tag);
      }

      card.appendChild(metaWrapper);
    }
  }

  // Card footer with action button(s)
  const cardFooter = document.createElement('div');
  cardFooter.className = 'pms-browse-card-footer';

  // Detect if this is a product card (should show "Add to Cart")
  const browseCardType = item.price ? detectBrowseCardType(item) : null;
  const showAddToCart = browseCardType === 'product' && !!item.title;
  const productUrl = item.metadata?.['product_url'] || item.metadata?.['url'];

  const hasExplicitAction = item.action;
  let actionValue = hasExplicitAction ? (item.action!.value || defaultActionValue) : defaultActionValue;

  // Append item title to tool/view-detail actions so panel.ts can send
  // natural messages like "Tell me about Club X" instead of "Show me details for id 2"
  if (item.title && (actionValue.startsWith('tool:') || actionValue.startsWith('view-detail:'))) {
    actionValue = `${actionValue}|${item.title}`;
  }
  const explicitUrl = hasExplicitAction ? item.action!.url : undefined;

  const platform = getCartPlatform();
  const isLinkPlatform = platform === 'wix' || platform === 'webflow';
  let footerHtml = '';

  if (isLinkPlatform && productUrl) {
    // ── Wix / Webflow: "Tell Me More" (ask AI) + "View Product" (link) ──
    cardFooter.classList.add('pms-browse-card-footer-split');
    footerHtml += `<button class="pms-browse-card-action" data-action="${escapeHtml(defaultActionValue)}">Tell Me More</button>`;
    footerHtml += `<button class="pms-browse-card-cart pms-browse-card-visit" data-link-url="${escapeHtml(productUrl)}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
      <span class="pms-browse-card-cart-text">View Product</span>
    </button>`;
  } else if (!isLinkPlatform && showAddToCart) {
    // ── Shopify / other: "View Details" (link or AI) + "Add to Cart" ──
    cardFooter.classList.add('pms-browse-card-footer-split');
    const viewUrl = explicitUrl || productUrl;
    if (viewUrl) {
      footerHtml += `<button class="pms-browse-card-action" data-link-url="${escapeHtml(viewUrl)}">View Details</button>`;
    } else {
      footerHtml += `<button class="pms-browse-card-action" data-action="${escapeHtml(actionValue)}">View Details</button>`;
    }
    footerHtml += `<button class="pms-browse-card-cart" data-card-id="${escapeHtml(cardId)}">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
      </svg>
      <span class="pms-browse-card-cart-text">${t('blocks.addToCart')}</span>
    </button>`;
  } else {
    // ── Generic / no product URL: single action button ──
    const actionLabel = hasExplicitAction ? (item.action!.label || 'View') : 'View Details';
    const actionUrl = explicitUrl || productUrl;
    if (actionUrl) {
      footerHtml += `<button class="pms-browse-card-action" data-link-url="${escapeHtml(actionUrl)}">${escapeHtml(actionLabel)}</button>`;
    } else if (actionValue.startsWith('link:')) {
      const linkUrl = actionValue.substring(5);
      footerHtml += `<button class="pms-browse-card-action" data-link-url="${escapeHtml(linkUrl)}">${escapeHtml(actionLabel)}</button>`;
    } else {
      footerHtml += `<button class="pms-browse-card-action" data-action="${escapeHtml(actionValue)}">${escapeHtml(actionLabel)}</button>`;
    }
  }

  cardFooter.innerHTML = footerHtml;
  card.appendChild(cardFooter);

  // Card click handling
  card.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    // Don't handle if clicking on action button, cart button, link, or checkbox
    if (target.closest('.pms-browse-card-action') ||
        target.closest('.pms-browse-card-cart') ||
        target.closest('.pms-browse-checkbox-wrapper') ||
        target.closest('a')) {
      return;
    }

    // If selectable, toggle selection on card click
    if (isSelectable) {
      const checkbox = card.querySelector('.pms-browse-checkbox') as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  // Bind link button clicks — navigates in-place for same-origin, new tab for cross-origin
  card.querySelectorAll<HTMLButtonElement>('[data-link-url]').forEach(linkBtn => {
    linkBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const url = linkBtn.dataset['linkUrl'];
      if (url) navigateToUrl(url);
    });
  });

  // Bind action button click with spam protection
  const actionBtn = card.querySelector('.pms-browse-card-action[data-action]') as HTMLButtonElement | null;
  if (actionBtn && onAction) {
    actionBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      disableButtonTemporarily(actionBtn, 2000);
      const action = actionBtn.dataset['action'];
      if (action) onAction(action);
    });
  }

  // Bind "Add to Cart" button (not visit-product links)
  const cartBtn = card.querySelector('.pms-browse-card-cart[data-card-id]') as HTMLButtonElement | null;
  if (cartBtn) {
    cartBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      handleBrowseAddToCartClick(item, cardId, cartBtn, onAction);
    });
  }

  return card;
}

/**
 * Detect item type from browse card data.
 * Only 'product' type gets "Add to Cart" — players, vehicles, properties, etc. don't.
 */
function detectBrowseCardType(item: BrowseCard): ListItem['type'] {
  const titleLower = (item.title || '').toLowerCase();
  const descLower = (item.description || '').toLowerCase();
  const badgeLower = (item.badge || '').toLowerCase();
  const metaType = item.metadata?.['type']?.toLowerCase() || '';
  const metaCategory = item.metadata?.['category']?.toLowerCase() || '';
  const typeHints = `${badgeLower} ${metaType} ${metaCategory}`;

  if (typeHints.includes('player') || typeHints.includes('athlete') || typeHints.includes('coach')
      || typeHints.includes('salary') || typeHints.includes('wage')) {
    return 'player';
  }
  if (typeHints.includes('rune') || typeHints.includes('item') || typeHints.includes('build')
      || typeHints.includes('gear') || typeHints.includes('loadout')) {
    return 'build';
  }
  if (typeHints.includes('service') || typeHints.includes('appointment') || typeHints.includes('booking')) {
    return 'service';
  }
  if (/\bcar\b/.test(titleLower) || descLower.includes('vehicle') || descLower.includes('mileage')
      || typeHints.includes('vehicle')) {
    return 'vehicle';
  }
  if (titleLower.includes('house') || titleLower.includes('property') || descLower.includes('bedroom')
      || typeHints.includes('property')) {
    return 'property';
  }
  if (item.price) {
    return 'product';
  }
  return 'generic';
}

/**
 * Handle "Add to Cart" click on browse product cards.
 */
async function handleBrowseAddToCartClick(
  item: BrowseCard,
  cardId: string,
  button: HTMLButtonElement,
  onAction?: (value: string) => void
): Promise<void> {
  if (button.disabled) return;
  button.disabled = true;

  const textEl = button.querySelector('.pms-browse-card-cart-text');
  const originalText = textEl?.textContent ?? t('blocks.addToCart');

  if (textEl) textEl.textContent = t('blocks.adding');
  button.classList.add('pms-browse-card-cart-loading');

  // Parse price
  let priceValue: number | undefined;
  let currency = 'USD';
  if (item.price) {
    const priceMatch = item.price.match(/([£$€])?([\d,]+(?:\.\d{1,2})?)/);
    if (priceMatch?.[2]) {
      priceValue = parseFloat(priceMatch[2].replace(/,/g, ''));
      const sym = priceMatch[1];
      if (sym === '£') currency = 'GBP';
      else if (sym === '€') currency = 'EUR';
    }
  }

  const listItem: ListItem = {
    id: cardId,
    type: 'product',
    title: item.title || 'Untitled',
    subtitle: item.description,
    price: priceValue,
    currency,
    image: item.image,
    metadata: item.metadata as Record<string, unknown> | undefined,
    addedAt: Date.now(),
  };

  // Add to internal list store
  const listStore = getListStore();
  if (!listStore.hasItem(cardId)) {
    listStore.addItem({
      id: cardId,
      type: 'product',
      title: item.title || 'Untitled',
      subtitle: item.description,
      price: priceValue,
      currency,
      image: item.image,
      metadata: item.metadata as Record<string, unknown> | undefined,
    });
  }

  const cartIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
  </svg>`;
  const checkIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
    <polyline points="20 6 9 17 4 12"/>
  </svg>`;

  try {
    const result = await addToCart([listItem]);
    if (!result || !result.success) {
      // No cart integration available (unknown platform) or add failed — show product URL if available
      const productUrl = (item.metadata as Record<string, string> | undefined)?.['product_url']
        || (item.metadata as Record<string, string> | undefined)?.['url'];
      if (productUrl) {
        navigateToUrl(productUrl);
      }
      button.classList.remove('pms-browse-card-cart-loading');
      button.innerHTML = `${cartIcon}<span class="pms-browse-card-cart-text">${escapeHtml(originalText)}</span>`;
      button.disabled = false;
      return;
    }
    button.classList.remove('pms-browse-card-cart-loading');
    button.classList.add('pms-browse-card-cart-success');
    button.innerHTML = `${checkIcon}<span class="pms-browse-card-cart-text">${t('blocks.added')}</span>`;
    if (onAction) onAction(`cart:add:${cardId}`);

    setTimeout(() => {
      button.disabled = false;
      button.classList.remove('pms-browse-card-cart-success');
      button.innerHTML = `${cartIcon}<span class="pms-browse-card-cart-text">${escapeHtml(originalText)}</span>`;
    }, 3000);
  } catch {
    button.classList.remove('pms-browse-card-cart-loading');
    button.classList.add('pms-browse-card-cart-error');
    if (textEl) textEl.textContent = 'Failed';

    setTimeout(() => {
      button.classList.remove('pms-browse-card-cart-error');
      button.innerHTML = `${cartIcon}<span class="pms-browse-card-cart-text">${escapeHtml(originalText)}</span>`;
      button.disabled = false;
    }, 2000);
  }
}
